import { useEffect } from 'react'
import { useRecoilValue } from 'recoil'
import {
  Route,
  Routes as ReactRouterDomRoutes,
  useLocation,
  useNavigate,
  Navigate,
} from 'react-router-dom'
import {
  Blank,
  CampaignManagement,
  DiscountIssue,
  EvChargingStation,
  ForgotPassword,
  FrequencyQuestions,
  Help,
  Login,
  PolicyDetail,
  PolicyManagement,
  ResetPassword,
  TaTransaction,
  TaTransactionOrder,
  TviConnect,
  Unauthorized,
  UserNotFound,
  UserPermission,
  WatchManagement,
  WelcomeCall,
  WellBeing,
  WellBeingPartner,
  Report,
  CheckTviConnect,
  Dopa,
  ReturnTime,
  AutoAssign,
  HealthClaim,
  HealthClaimDetail,
  CrashDetectionDetail,
  CrashDetection,
  CrashDetectionMonitor,
} from '@/pages'
import CONSTANT from '../../constant'
import RequireAuth from './RequireAuth'
import { BACKOFFICE_USER_PAGE } from '../../constant/BACKOFFICE_PERMISSION'
import { HealthClaimModeEnum } from '@/constant/E_CLAIM'
import userAtom from '@/recoil/user'
import { routes, resolveLandingPath } from './constants'
import HealthClaimApproval from '@/pages/HealthClaimApproval'

/**
 * Resolve the first route an authenticated user is actually allowed to see,
 * based on the order of `user.pages`, and redirect there. This replaces a
 * hardcoded `/ → POLICY_DETAIL` jump (which bounced any user lacking
 * CUS0101/MTP0101 to /unauthorized) and is declarative, so it can't race with
 * the pathname-driven effect below the way the old setTimeout redirect did.
 */
function LandingRedirect() {
  const user = useRecoilValue(userAtom)

  // wait until auth state is known
  if (!user || typeof user.isAuth === 'undefined') return null

  if (!user.isAuth) {
    return <Navigate to={CONSTANT.ROUTES.LOGIN_PAGE} replace />
  }

  return <Navigate to={resolveLandingPath(user.pages)} replace />
}

function Routes() {
  const location = useLocation()
  const navigate = useNavigate()
  const user = useRecoilValue(userAtom)

  useEffect(() => {
    // Wait for user to be loaded
    if (!user || typeof user.isAuth === 'undefined') return

    // Authenticated users are routed by <LandingRedirect /> (the "/" route) and
    // by each route's <RequireAuth>. Nothing to do here.
    if (user.isAuth) return

    // ถ้าไม่ล็อกอิน ให้ไปหน้า login (ยกเว้นถ้าอยู่หน้า public หรืออยู่หน้า login อยู่แล้ว)
    const currentPath = location.pathname || '/'
    const isPublic = routes.some(
      (r) => !r.isAuth && currentPath.startsWith((r.path || '').split('/:')[0]),
    )

    if (!isPublic && currentPath !== CONSTANT.ROUTES.LOGIN_PAGE) {
      navigate(CONSTANT.ROUTES.LOGIN_PAGE, { replace: true })
    }
  }, [user?.isAuth, location.pathname, navigate])

  return (
    <ReactRouterDomRoutes>
      <Route path={CONSTANT.ROUTES.UNAUTHORIZED_PAGE} element={<Unauthorized />} />
      <Route path={CONSTANT.PATHNAME.STAR} element={<Blank />} key={0} />
      <Route path={CONSTANT.PATHNAME.LOGIN} element={<Login />} key={1} />
      <Route path={CONSTANT.ROUTES.PASSWORD_FORGOT_PAGE} element={<ForgotPassword />} key={2} />
      <Route path={CONSTANT.ROUTES.PASSWORD_RESET_PAGE} element={<ResetPassword />} key={3} />
      <Route path={CONSTANT.ROUTES.CONFIRM_EMAIL_PAGE} element={<ForgotPassword />} key={4} />
      <Route
        path={CONSTANT.ROUTES.HOME_PAGE}
        element={<LandingRedirect />}
        key={5}
      />
      <Route
        path={CONSTANT.ROUTES.TVI_CONNECT_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.MTD0101]}>
            <TviConnect />
          </RequireAuth>
        }
        key={5}
      />
      <Route
        path={CONSTANT.ROUTES.EV_CHARGING_STATION_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.MTD0101]}>
            <EvChargingStation />
          </RequireAuth>
        }
        key={6}
      />
      <Route
        path={CONSTANT.PATHNAME.POLICY_DETAIL}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.CUS0101, BACKOFFICE_USER_PAGE.MTP0101]}>
            <PolicyDetail />
          </RequireAuth>
        }
        key={7}
      >
        <Route
          path={CONSTANT.PARAMS.SEARCH_INPUT}
          element={
            <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.CUS0101, BACKOFFICE_USER_PAGE.MTP0101]}>
              <PolicyDetail />
            </RequireAuth>
          }
        />
      </Route>
      <Route
        path={CONSTANT.ROUTES.REPORT_PAGE}
        element={
          <RequireAuth pageCode={undefined}>
            <Report />
          </RequireAuth>
        }
        key={8}
      />
      <Route
        path={CONSTANT.ROUTES.WELCOME_CALL_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.WEL0101]}>
            <WelcomeCall />
          </RequireAuth>
        }
        key={9}
      />
      <Route
        path={CONSTANT.ROUTES.POLICY_MANAGEMENT_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.POL0101]}>
            <PolicyManagement />
          </RequireAuth>
        }
        key={10}
      />
      <Route
        path={CONSTANT.ROUTES.DISCOUNT_ISSUE_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.DIS0101]}>
            <DiscountIssue />
          </RequireAuth>
        }
        key={11}
      />
      <Route
        path={CONSTANT.ROUTES.WATCH_MANAGEMENT_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.WAT0101]}>
            <WatchManagement />
          </RequireAuth>
        }
        key={12}
      />
      <Route
        path={CONSTANT.ROUTES.CAMPAIGN_MANAGEMENT_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.CAM0101]}>
            <CampaignManagement />
          </RequireAuth>
        }
        key={13}
      />
      <Route
        path={CONSTANT.ROUTES.HELP_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.HEL0101]}>
            <Help />
          </RequireAuth>
        }
        key={14}
      />
      <Route
        path={CONSTANT.ROUTES.FREQUENCY_QUESTIONS_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.FAQ0101]}>
            <FrequencyQuestions />
          </RequireAuth>
        }
        key={15}
      />
      <Route
        path={CONSTANT.ROUTES.USER_PERMISSION_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.USR0101]}>
            <UserPermission />
          </RequireAuth>
        }
        key={16}
      />
      <Route
        path={CONSTANT.PATHNAME.WELL_BEING}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.WBE0101]}>
            <WellBeing />
          </RequireAuth>
        }
        key={17}
      >
        <Route
          path={CONSTANT.PARAMS.PARTNER_ID}
          element={
            <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.WBE0101]}>
              <WellBeingPartner />
            </RequireAuth>
          }
        />
      </Route>
      <Route
        path={CONSTANT.PATHNAME.TA_TRANSACTION}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.TA0101]}>
            <TaTransaction />
          </RequireAuth>
        }
        key={18}
      >
        <Route
          path={CONSTANT.PARAMS.ORDER_ID}
          element={
            <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.TA0102]}>
              <TaTransactionOrder />
            </RequireAuth>
          }
        />
      </Route>
      <Route
        path={CONSTANT.PATHNAME.RETURN_TIME}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.MTA0101]}>
            <ReturnTime />
          </RequireAuth>
        }
        key={19}
      />
      <Route
        path={CONSTANT.ROUTES.TVI_CONNECT_CHECK_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.MTD0201]}>
            <CheckTviConnect />
          </RequireAuth>
        }
        key={20}
      />
      <Route
        path={CONSTANT.PATHNAME.DOPA}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.DOP0101]}>
            <Dopa />
          </RequireAuth>
        }
        key={21}
      />
      <Route path={CONSTANT.ROUTES.USER_NOT_FOUND_PAGE} element={<UserNotFound />} key={22} />
      <Route
        path={CONSTANT.PATHNAME.AUTO_ASSIGN}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.AAS0101]}>
            <AutoAssign />
          </RequireAuth>
        }
        key={23}
      />
      <Route
        path={CONSTANT.ROUTES.HEALTH_CLAIM_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.CLM0101]}>
            <HealthClaim />
          </RequireAuth>
        }
        key={24}
      />
      <Route
        path={CONSTANT.ROUTES.HEALTH_CLAIM_APPROVAL}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.APP0101]}>
            <HealthClaimApproval mode={HealthClaimModeEnum.CREATE} />
          </RequireAuth>
        }
        key={25}
      />
      <Route
        path={CONSTANT.ROUTES.HEALTH_CLAIM_CREATE_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.CLM0101]}>
            <HealthClaimDetail mode={HealthClaimModeEnum.CREATE} />
          </RequireAuth>
        }
        key={26}
      />
      <Route
        path={CONSTANT.ROUTES.HEALTH_CLAIM_VIEW_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.CLM0101]}>
            <HealthClaimDetail mode={HealthClaimModeEnum.VIEW} />
          </RequireAuth>
        }
        key={27}
      />
      <Route
        path={CONSTANT.ROUTES.HEALTH_CLAIM_EDIT_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.CLM0101]}>
            <HealthClaimDetail mode={HealthClaimModeEnum.EDIT} />
          </RequireAuth>
        }
        key={28}
      />
      <Route
        path={CONSTANT.ROUTES.CRASH_DETECTION_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.CRS0101]}>
            <CrashDetection />
          </RequireAuth>
        }
        key={29}
      />
      <Route
        path={CONSTANT.ROUTES.CRASH_DETECTION_VIEW_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.CRS0101]}>
            <CrashDetectionDetail mode="view" />
          </RequireAuth>
        }
        key={30}
      />
      <Route
        path={CONSTANT.ROUTES.CRASH_DETECTION_EDIT_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.CRS0101]}>
            <CrashDetectionDetail mode="edit" />
          </RequireAuth>
        }
        key={31}
      />
      <Route
        path={CONSTANT.ROUTES.CRASH_DETECTION_MONITOR_PAGE}
        element={
          <RequireAuth pageCode={[BACKOFFICE_USER_PAGE.CRS0101]}>
            <CrashDetectionMonitor />
          </RequireAuth>
        }
        key={32}
      />
    </ReactRouterDomRoutes>
  )
}

export default Routes
